import naclUtil from 'tweetnacl-util';
import { sanitizeSupportDiagnosticString } from './SupportDiagnosticBuffer';
export const MEDIA_DIAGNOSTIC_MAX_EVENTS = 1000;
export const MEDIA_DIAGNOSTIC_MAX_BYTES = 256 * 1024;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const KEY = 'media_history_v1';
const PHASES = [
  'capture_received', 'local_file_ready', 'local_file_failed', 'prepare_started', 'prepared', 'prepare_failed',
  'prepare_reading', 'prepare_transaction', 'prepare_value', 'prepare_pending', 'prepare_commit', 'prepare_cleanup', 'prepare_operation',
  'capture_started', 'capture_cancelled', 'capture_denied', 'capture_failed',
  'drawing_started', 'save_requested', 'drawing_cleared', 'drawing_cancelled', 'drawing_empty',
  'upload_started', 'storage_uploaded', 'reference_committed', 'upload_failed', 'superseded',
  'reference_send_started', 'reference_send_returned', 'reference_send_failed',
  'reference_ack_deferred', 'reference_ack_failed', 'reference_remote_confirmed', 'reference_acknowledged',
  'cleanup_failed', 'tombstone_blocked', 'source_missing', 'remove_started', 'removed', 'remove_failed',
  'local_source_relocated', 'local_source_relocation_blocked',
] as const;
export type MediaDiagnosticPhase = typeof PHASES[number];
const SYNC_REASONS = [
  'response_received', 'request_failed', 'identity_mismatch', 'recovery_receipt_invalid',
  'path_mismatch', 'remote_read_failed', 'remote_reference_missing', 'remote_reference_mismatch',
  'snapshot_mismatch', 'storage_unconfirmed', 'local_cas_rejected', 'ack_exception',
  'local_reference_not_prepared', 'session_changed',
] as const;
export interface MediaSyncDiagnosticDetails {
  operationId?: string;
  valueId?: string;
  transport?: 'legacy_rest' | 'rpc';
  reason?: typeof SYNC_REASONS[number];
}
export interface MediaDiagnosticEvent extends MediaSyncDiagnosticDetails {
  at: number;
  phase: MediaDiagnosticPhase;
  checklistId: string;
  fieldId: string;
  attemptId?: string;
  errorCode?: string;
  errorText?: string;
  mediaRef?: string;
}
interface Store { getString(key: string): string | undefined; set(key: string, value: string): void; delete(key: string): void }
const safeId = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(value);
const safeSyncDetails = (input: MediaSyncDiagnosticDetails): MediaSyncDiagnosticDetails => ({
  ...(safeId(input.operationId) ? { operationId: input.operationId } : {}),
  ...(safeId(input.valueId) ? { valueId: input.valueId } : {}),
  ...(['legacy_rest', 'rpc'].includes(input.transport ?? '') ? { transport: input.transport } : {}),
  ...(SYNC_REASONS.includes(input.reason as any) ? { reason: input.reason } : {}),
});
export const mediaErrorCode = (error: unknown): string | undefined => {
  if (error === undefined) return undefined;
  const message = error instanceof Error ? error.message : String((error as any)?.message ?? error);
  if (/database (?:table |schema )?is locked|sqlite_(?:busy|locked)/i.test(message)) return 'database_locked';
  if (/quota|disk.*full|no space/i.test(message)) return 'storage_full';
  if (/not found|no such file|enoent|missing|(?:arquivo|imagem).*n[aã]o encontrad/i.test(message)) return 'source_missing';
  if (/network|timeout|fetch|conex|tempo de envio|offline|internet|disconnected|econn|enetunreach/i.test(message)) return 'network';
  if (/cursor|transaction.*(?:inactive|abort)|TransactionInactiveError|InvalidStateError/i.test(message)) return 'local_transaction';
  return 'other';
};

/** Referência útil ao suporte, sem bytes, query, credenciais ou diretório privado do aparelho. */
export const sanitizeMediaRef = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || /^data:/i.test(value.trim())) return undefined;
  if (/^blob:/i.test(value)) return 'blob';
  const path = value.split(/[?#]/)[0].match(/((?:checklists?-(?:photos|signatures|marcacoes))\/[A-Za-z0-9._/-]+)/i)?.[1];
  return path?.slice(0, 180);
};

export const sanitizeMediaErrorText = (error: unknown): string | undefined => {
  if (error == null) return undefined;
  try {
    const message = typeof error === 'string' ? error : String((error as { message?: unknown }).message ?? error);
    const clean = sanitizeSupportDiagnosticString(message)
      .replace(/(?:https?|file):\/\/[^\s]+/gi, '[location]')
      .replace(/data:[^\s]*/gi, '[data-url]')
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
      .replace(/[?&][^\s]*/g, '')
      .replace(/\s+/g, ' ').trim();
    return clean ? clean.slice(0, 240) : undefined;
  } catch { return 'Erro sem mensagem legível'; }
};

/** Histórico limitado de etapas, referências relativas e erros sanitizados, nunca imagens. */
export class MediaDiagnosticHistory {
  private identity: string | null = null;
  private events: MediaDiagnosticEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  private dirty = false;
  private store?: Store;
  constructor(private readonly createStore: () => Store, private readonly now = Date.now) {}

  bind(companyId?: string | null, userId?: string | null): void {
    // Identidade ainda carregando não equivale a logout nem autoriza hidratação.
    if (!safeId(companyId) || !safeId(userId)) return;
    const identity = `${companyId}:${userId}`;
    if (identity === this.identity) return;
    try {
      if (this.timer) clearTimeout(this.timer);
      this.timer = undefined;
      this.identity = identity;
      this.events = [];
      this.store ??= this.createStore();
      const raw = this.store.getString(KEY);
      if (raw && raw.length <= MEDIA_DIAGNOSTIC_MAX_BYTES) {
        const saved = JSON.parse(raw);
        if (saved.schema === 1 && saved.identity === identity && Array.isArray(saved.events)) {
          this.events = saved.events.filter((event: any) => Number.isFinite(event?.at) &&
            event.at <= this.now() && event.at >= this.now() - TTL_MS && PHASES.includes(event.phase) &&
            safeId(event.checklistId) && safeId(event.fieldId) && (!event.attemptId || safeId(event.attemptId)))
            .slice(-MEDIA_DIAGNOSTIC_MAX_EVENTS).map((event: MediaDiagnosticEvent) => ({
              at: event.at, phase: event.phase, checklistId: event.checklistId, fieldId: event.fieldId,
              ...safeSyncDetails(event),
              ...(event.attemptId ? { attemptId: event.attemptId } : {}),
              ...(sanitizeMediaErrorText(event.errorText) ? { errorText: sanitizeMediaErrorText(event.errorText) } : {}),
              ...(sanitizeMediaRef(event.mediaRef) ? { mediaRef: sanitizeMediaRef(event.mediaRef) } : {}),
              ...(['database_locked', 'storage_full', 'source_missing', 'network', 'local_transaction', 'other'].includes(event.errorCode || '') ? { errorCode: event.errorCode } : {}),
            }));
        }
      }
      this.dirty = true;
      this.flush();
    } catch { /* Diagnóstico não bloqueia abertura nem captura. */ }
  }

  recorder(input: { checklistId: string; fieldId: string; attemptId?: string; mediaRef?: string }): (phase: MediaDiagnosticPhase, error?: unknown) => void {
    const identity = this.identity;
    return (phase, error) => {
      if (identity && identity === this.identity) this.record(phase, { ...input, error });
    };
  }

  /** Somente evidência: não modifica o journal de captura, as filas ou o resultado do sync. */
  syncRecorder(input: MediaSyncDiagnosticDetails & { companyId: string; userId: string; checklistId: string; fieldId: string; attemptId?: string; mediaRef?: string }) {
    const identity = this.identity;
    return (phase: MediaDiagnosticPhase, reason?: MediaSyncDiagnosticDetails['reason'], error?: unknown): void => {
      if (identity && identity === this.identity && identity === `${input.companyId}:${input.userId}`) {
        this.record(phase, { ...input, reason, error });
      }
    };
  }

  record(phase: MediaDiagnosticPhase, input: MediaSyncDiagnosticDetails & { checklistId: string; fieldId: string; attemptId?: string; mediaRef?: string; error?: unknown }): void {
    if (!this.identity) return;
    try {
      if (!PHASES.includes(phase) || !safeId(input.checklistId) || !safeId(input.fieldId)) return;
      const event: MediaDiagnosticEvent = { at: this.now(), phase, checklistId: input.checklistId, fieldId: input.fieldId,
        ...safeSyncDetails(input),
        ...(safeId(input.attemptId) ? { attemptId: input.attemptId } : {}),
        ...(input.error !== undefined ? { errorCode: mediaErrorCode(input.error), errorText: sanitizeMediaErrorText(input.error) } : {}),
        ...(sanitizeMediaRef(input.mediaRef) ? { mediaRef: sanitizeMediaRef(input.mediaRef) } : {}),
      };
      // Polls iguais não ocupam o histórico inteiro; mudança de motivo continua visível.
      if (phase === 'reference_ack_deferred') {
        const previous = [...this.events].reverse().find(row => row.checklistId === event.checklistId &&
          row.fieldId === event.fieldId && row.attemptId === event.attemptId && row.valueId === event.valueId);
        if (previous?.phase === phase && previous.reason === event.reason &&
            previous.errorCode === event.errorCode && event.at - previous.at < 60_000) return;
      }
      this.events.push(event);
      if (this.events.length > MEDIA_DIAGNOSTIC_MAX_EVENTS) this.events.shift();
      this.dirty = true;
      // Janela fixa desde o primeiro evento: fluxo contínuo não adia indefinidamente a gravação.
      this.timer ??= setTimeout(() => { this.timer = undefined; this.flush(); }, 1000);
    } catch { /* Até erro de serialização/classificação deve falhar aberto. */ }
  }

  flush(): void {
    if (!this.identity || !this.dirty) return;
    try {
      this.store ??= this.createStore();
      this.events = this.events.filter(event => event.at >= this.now() - TTL_MS);
      let raw = JSON.stringify({ schema: 1, identity: this.identity, events: this.events });
      // Erros podem conter Unicode; considera o tamanho real em UTF-8.
      while (naclUtil.decodeUTF8(raw).length > MEDIA_DIAGNOSTIC_MAX_BYTES && this.events.length) {
        this.events.shift();
        raw = JSON.stringify({ schema: 1, identity: this.identity, events: this.events });
      }
      this.store.set(KEY, raw);
      this.dirty = false;
    } catch { /* Próximo evento ou background tenta novamente, sem recursão de logs. */ }
  }

  snapshot(companyId: string, userId: string): MediaDiagnosticEvent[] {
    if (`${companyId}:${userId}` !== this.identity) return [];
    this.flush();
    return this.events.filter(event => event.at >= this.now() - TTL_MS).map(event => ({ ...event }));
  }

  clear(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.identity = null;
    this.events = [];
    this.dirty = false;
    try { this.store ??= this.createStore(); this.store.delete(KEY); } catch { /* Snapshot antigo continua isolado pela identidade. */ }
  }
}
